import { useState, useMemo, useCallback } from 'react';
import {
    raiseError,
    startMessagingVerificationConfig,
    useContactDataClick,
    useCurrentPageName,
    useIsActivationCompletedAtom,
} from '@fiverr-private/seller_pages_toolbox';
import { guardWithActivation } from '@fiverr-private/code_activation_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { useIsLessThanMdScreen } from '../useIsLessThanMdScreen';
import { useAgencySellerData } from '../useAgencySellerData';
import { useFloatingChatTrigger } from '../useFloatingChatTrigger';
import { AgencySeller } from '../../types';

const ACTIVATION_SOURCE = 'agency_page_contact';

export const useContactSellerModalData = ({ isAnchorVisible }: { isAnchorVisible: boolean }) => {
    const { userId } = getContext();
    const isLoggedIn = !!userId;
    const [showModal, setShowModal] = useState(false);
    const currentPageName = useCurrentPageName();
    const { sellerId, username, displayName } = useAgencySellerData(selectSellerSlice);
    const isContactDisabled = String(userId) === sellerId;
    const conversationUrl = pathfinder('conversation', { id: username });
    const userHasPendingState = !useIsActivationCompletedAtom();
    const isLessThanMdScreen = useIsLessThanMdScreen();
    const { isFloatingChatEnabled, openFloatingChat } = useFloatingChatTrigger();
    const { modalInitialData, setIsContactClicked, refetchInitialData, isContactClickReady } = useContactDataClick({
        username,
        isVisible: isAnchorVisible,
        isLoggedIn,
        isMD: !isLessThanMdScreen,
        userHasPendingState,
        onError: () => raiseError(),
    });
    const [hasPriorInteraction, setHasPriorInteraction] = useState(false);

    const shouldRedirectToInbox = useMemo(
        () =>
            typeof window !== 'undefined' &&
            (isLessThanMdScreen || modalInitialData?.priorInteraction || hasPriorInteraction),
        [isLessThanMdScreen, modalInitialData, hasPriorInteraction]
    );
    const redirectToInbox = useCallback(() => window?.location.assign(conversationUrl), [conversationUrl]);

    const onContactOpen = useCallback(() => setShowModal(true), []);

    const onContactClick = async () => {
        if (shouldRedirectToInbox) {
            redirectToInbox();
        }

        setIsContactClicked(true);

        const maxWaitingTime = new Promise<undefined>((resolve) => {
            setTimeout(() => resolve(undefined), 500);
        });
        const data = isContactClickReady();

        try {
            const additionalData = await Promise.race([maxWaitingTime, data]);
            if (additionalData !== undefined && 'priorInteraction' in additionalData) {
                setHasPriorInteraction(!!additionalData?.priorInteraction);
            }
        } catch (error) {
            logger.error('GeneralContactSellerModal: Failed to get isContactClickReady data', error as Error);
        }

        if (shouldRedirectToInbox) {
            redirectToInbox();
        }

        if (!isLoggedIn) {
            window.openSignUpModal?.({}, { source: currentPageName });
            return;
        }

        await guardWithActivation({
            onSuccess: initiateContact,
            sourceComponent: ACTIVATION_SOURCE,
            phoneVerificationConfig: startMessagingVerificationConfig,
        });
    };

    const initiateContact = () => {
        if (isFloatingChatEnabled) {
            openFloatingChat();
        } else {
            onContactOpen();
        }
    };

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, [setShowModal]);

    const modalProps = useMemo(() => {
        const recipientInfo = modalInitialData?.recipient ?? {};
        const show = showModal && !shouldRedirectToInbox;

        return {
            pageName: currentPageName,
            isOpen: show,
            recipientInfo: {
                ...recipientInfo,
                sellerId,
                username,
                displayName,
            },
            attachmentsUploadConfig: modalInitialData?.attachmentsUploadConfig,
            showInfo: true,
            source: 'agency_page_contact_me',
            onSubmitSuccess: refetchInitialData,
            onClose: handleCloseModal,
        };
    }, [
        refetchInitialData,
        modalInitialData,
        handleCloseModal,
        sellerId,
        username,
        showModal,
        currentPageName,
        displayName,
        shouldRedirectToInbox,
    ]);

    return {
        showContactSellerModal: showModal,
        contactSellerModalProps: modalProps,
        onContactClick,
        isContactDisabled,
    };
};

const selectSellerSlice = ({ user: { id, name, profile } }: AgencySeller) => ({
    sellerId: id,
    username: name,
    displayName: profile.displayName,
});
